import React, { useEffect, useMemo, useState } from "react";
import styles from "./popularProd.module.css";
import { ArrowRight, ChevronLeft, ChevronRight, Cpu, ShoppingCart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addToCartThunk } from "../../../../redux/cartSlice";
import { setLoginpopup } from "../../../../redux/customerSlice";
import { stripHtmlTags } from "../../../../utils/utils";

const PRODUCTS_PER_PAGE = 4;

// Standard packaging / minimum order quantity for a product, so "Add to cart"
// uses a valid default quantity instead of 1 (the backend enforces SPQ multiples).
const spqOf = (item) => {
  const fromInventory = Number(item?.inventory?.minimumMultiplier);
  if (fromInventory > 0) return fromInventory;
  const packaging = item?.additionalResources?.Standard_Packaging_Quantity;
  const fromPackaging = Number(Array.isArray(packaging) ? packaging[0] : packaging);
  return fromPackaging > 0 ? fromPackaging : 1;
};

const Brand1Slider = ({ prod = [], manufacturer }) => {
  const [page, setPage] = useState(0);
  const [addingId, setAddingId] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedin = useSelector((state) => state.customer?.isLoggedin);
  const products = useMemo(() => prod.filter(Boolean), [prod]);
  const pageCount = Math.max(1, Math.ceil(products.length / PRODUCTS_PER_PAGE));
  const visibleProducts = products.slice(page * PRODUCTS_PER_PAGE, (page + 1) * PRODUCTS_PER_PAGE);

  useEffect(() => setPage(0), [manufacturer]);
  const movePage = (direction) => setPage((current) => (current + direction + pageCount) % pageCount);

  // Add straight to the cart from the showcase. Visitors who are not signed in
  // get the same login prompt the product page uses.
  const handleAddToCart = async (item) => {
    if (!item?._id || addingId) return;
    if (!isLoggedin) {
      dispatch(setLoginpopup(true));
      return;
    }
    const quantity = spqOf(item);
    setAddingId(item._id);
    try {
      await dispatch(addToCartThunk([{ productId: item._id, totalQty: quantity, quantity }]));
    } finally {
      setAddingId(null);
    }
  };

  if (!products.length) {
    return (
      <div className={styles.emptySelection}>
        <div className={styles.emptyIcon}><Cpu size={24} /></div>
        <div>
          <span className={styles.emptyEyebrow}>Live catalogue</span>
          <h3>Fresh picks are being prepared</h3>
          <p>Our team is updating this manufacturer’s most in-demand components.</p>
        </div>
        <span className={styles.emptyPulse} aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className={styles.productShowcase}>
      <div className={styles.productGrid} key={`${manufacturer}-${page}`}>
        {visibleProducts.map((item, index) => {
          const name = item?.name || "Electronic component";
          const description = stripHtmlTags(Array.isArray(item?.description) ? item.description[0] : item?.description) || "Verified component specifications and sourcing information.";
          const isAdding = addingId === item?._id;
          return (
            <article className={styles.prodCard} key={item?._id || `${name}-${index}`}>
              <div className={styles.cardImageContainer}>
                <img src={item?.image} alt={name} className={styles.cardImage} onError={(event) => { event.currentTarget.style.display = "none"; }} />
                <span className={styles.imageFallback}><Cpu size={34} /></span>
              </div>
              <div className={styles.cardBody}>
                <h3 className={styles.cardTitle} title={name}>{name}</h3>
                <p className={styles.cardDesc} title={description}>{description}</p>
              </div>
              <div className={styles.cardActions}>
                <button
                  className={styles.addCartBtn}
                  onClick={() => handleAddToCart(item)}
                  disabled={isAdding}
                >
                  <ShoppingCart size={15} /> {isAdding ? "Adding…" : "Add to cart"}
                </button>
                <button className={styles.viewCardBtn} onClick={() => navigate(`/ProductCode/${name}`)}>
                  View details <ArrowRight size={14} />
                </button>
              </div>
            </article>
          );
        })}
      </div>
      {pageCount > 1 && (
        <div className={styles.productControls}>
          <div className={styles.pageMeter} aria-hidden="true">
            {Array.from({ length: pageCount }).map((_, index) => <span key={index} className={index === page ? styles.activeMeter : ""} />)}
          </div>
          <div className={styles.sliderArrows}>
            <button className={styles.arrowBtn} onClick={() => movePage(-1)} aria-label="Previous products"><ChevronLeft size={18} /></button>
            <button className={`${styles.arrowBtn} ${styles.arrowNextActive}`} onClick={() => movePage(1)} aria-label="Next products"><ChevronRight size={18} /></button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Brand1Slider;
